import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('service', formData.service);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6cgmlbv53dtlm7j84o0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="inline-block px-6 py-2 rounded-full border-2 border-[#6B8E6F] mb-6">
              <span className="text-[#6B8E6F] text-sm font-medium">Get In Touch</span>
            </div>
            <h2 className="text-5xl font-serif text-[#2C2C2C] mb-6">Let's Start Your Care Journey</h2>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Reach out to us for a free consultation. We'll discuss your needs and create a personalized care plan for your loved one.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[#FAF8F5] flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-[#6B8E6F] text-xl"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-[#2C2C2C] mb-1">Phone</h3>
                  <a href="tel:+19547162020" className="text-gray-600 hover:text-[#6B8E6F] transition-colors cursor-pointer">
                    (954) 716-2020
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[#FAF8F5] flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-[#6B8E6F] text-xl"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-[#2C2C2C] mb-1">Email</h3>
                  <a href="mailto:info@gentlerootshomecare.com" className="text-gray-600 hover:text-[#6B8E6F] transition-colors cursor-pointer">
                    info@gentlerootshomecare.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[#FAF8F5] flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-[#6B8E6F] text-xl"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-[#2C2C2C] mb-1">Address</h3>
                  <p className="text-gray-600">
                    800 Battery Avenue SE, Suite 300<br />
                    Office #3076<br />
                    Atlanta, GA 30339
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[#FAF8F5] flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-[#6B8E6F] text-xl"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-[#2C2C2C] mb-1">Hours</h3>
                  <p className="text-gray-600">
                    24/7 Care Available<br />
                    Office: Mon-Fri 8am-6pm
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[#FAF8F5] rounded-2xl p-8">
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-[#2C2C2C] mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#6B8E6F] focus:ring-2 focus:ring-[#6B8E6F]/20 outline-none transition-all text-sm"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-[#2C2C2C] mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#6B8E6F] focus:ring-2 focus:ring-[#6B8E6F]/20 outline-none transition-all text-sm"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-[#2C2C2C] mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#6B8E6F] focus:ring-2 focus:ring-[#6B8E6F]/20 outline-none transition-all text-sm"
                  placeholder="(123) 456-7890"
                />
              </div>

              <div>
                <label htmlFor="service" className="block text-sm font-medium text-[#2C2C2C] mb-2">
                  Service Interested In *
                </label>
                <select
                  id="service"
                  name="service"
                  required
                  value={formData.service}
                  onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#6B8E6F] focus:ring-2 focus:ring-[#6B8E6F]/20 outline-none transition-all text-sm"
                >
                  <option value="">Select a service</option>
                  <option value="Personal Care">Personal Care</option>
                  <option value="Companionship">Companionship</option>
                  <option value="Meal Preparation">Meal Preparation</option>
                  <option value="Light Housekeeping">Light Housekeeping</option>
                  <option value="Medication Reminders">Medication Reminders</option>
                  <option value="Transportation">Transportation</option>
                  <option value="Respite Care">Respite Care</option>
                  <option value="Dementia Care">Dementia Care</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-[#2C2C2C] mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  maxLength={500}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#6B8E6F] focus:ring-2 focus:ring-[#6B8E6F]/20 outline-none transition-all resize-none text-sm"
                  placeholder="Tell us about your care needs..."
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-[#6B8E6F] text-white rounded-lg font-medium hover:bg-[#5a7a5e] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>

              {submitStatus === 'success' && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
                  Thank you! We'll contact you soon.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                  Something went wrong. Please try again.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
